interface GuestScenario {
  data: any;
  timestamp: number;
  expires: number;
}

class GuestStorageService {
  private readonly COOKIE_PREFIX = 'larryshelp_guest_';
  private readonly EXPIRY_HOURS = 24;

  /**
   * Save a scenario for guest users using cookies
   */
  saveGuestScenario(calculatorType: string, data: any): void {
    const scenario: GuestScenario = {
      data,
      timestamp: Date.now(),
      expires: Date.now() + (this.EXPIRY_HOURS * 60 * 60 * 1000)
    };

    const cookieValue = JSON.stringify(scenario);
    const cookieName = `${this.COOKIE_PREFIX}${calculatorType}`;
    
    // Set cookie with 24-hour expiry
    document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; max-age=${this.EXPIRY_HOURS * 60 * 60}; path=/; SameSite=Lax`;
  }

  /**
   * Retrieve a guest scenario from cookies
   */
  getGuestScenario(calculatorType: string): any | null {
    const cookieName = `${this.COOKIE_PREFIX}${calculatorType}`;
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName && value) {
        try {
          const scenario: GuestScenario = JSON.parse(decodeURIComponent(value));
          
          // Check if scenario has expired
          if (Date.now() > scenario.expires) {
            this.clearGuestScenario(calculatorType);
            return null;
          }
          
          return scenario.data;
        } catch (error) {
          console.error('Error parsing guest scenario cookie:', error);
          this.clearGuestScenario(calculatorType);
          return null;
        }
      }
    }
    
    return null;
  }

  /**
   * Clear a specific guest scenario
   */
  clearGuestScenario(calculatorType: string): void {
    const cookieName = `${this.COOKIE_PREFIX}${calculatorType}`;
    document.cookie = `${cookieName}=; max-age=0; path=/`;
  }

  /**
   * Clear all guest scenarios
   */
  clearAllGuestScenarios(): void {
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name] = cookie.trim().split('=');
      if (name && name.startsWith(this.COOKIE_PREFIX)) {
        document.cookie = `${name}=; max-age=0; path=/`;
      }
    }
  }

  /**
   * Check if a guest scenario exists and is valid
   */
  hasValidGuestScenario(calculatorType: string): boolean {
    return this.getGuestScenario(calculatorType) !== null;
  }

  /**
   * Get all valid guest scenarios
   */
  getAllGuestScenarios(): Record<string, any> {
    const scenarios: Record<string, any> = {};
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name && name.startsWith(this.COOKIE_PREFIX) && value) {
        const calculatorType = name.replace(this.COOKIE_PREFIX, '');
        const scenario = this.getGuestScenario(calculatorType);
        if (scenario) {
          scenarios[calculatorType] = scenario;
        }
      }
    }
    
    return scenarios;
  }

  /**
   * Get remaining time for a guest scenario in hours
   */
  getRemainingTime(calculatorType: string): number {
    const cookieName = `${this.COOKIE_PREFIX}${calculatorType}`;
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName && value) {
        try {
          const scenario: GuestScenario = JSON.parse(decodeURIComponent(value));
          const remainingMs = scenario.expires - Date.now();
          return Math.max(0, Math.ceil(remainingMs / (60 * 60 * 1000)));
        } catch (error) {
          return 0;
        }
      }
    }
    
    return 0;
  }
}

export const guestStorageService = new GuestStorageService();
export default guestStorageService; 